import { query, orderBy, limit, startAfter, getDocs, where, collection } from 'firebase/firestore';
import { useState, useCallback, useEffect } from 'react';

/**
 * Query performance monitoring utility
 * Tracks query execution time and result metrics
 */
export function monitorQuery(queryName, queryFn) {
  const start = performance.now();
  
  return queryFn().then(result => {
    const duration = performance.now() - start;
    
    // Only log slow queries (> 1000ms) in development
    if (process.env.NODE_ENV === 'development' && duration > 1000) {
      console.warn(`[Query] ⚠️ ${queryName}: ${duration.toFixed(2)}ms`);
    }
    
    // Send to analytics if available
    if (window.gtag) {
      window.gtag('event', 'query_performance', {
        custom_parameter_1: queryName,
        custom_parameter_2: Math.round(duration),
        custom_parameter_3: result?.docs?.length || result?.length || 0
      });
    }
    
    return result;
  }).catch(error => {
    const duration = performance.now() - start;
    console.error(`[Query Error] ${queryName}: ${error.message} (${duration.toFixed(2)}ms)`);
    
    // Send error to analytics
    if (window.gtag) {
      window.gtag('event', 'query_error', {
        custom_parameter_1: queryName,
        custom_parameter_2: Math.round(duration),
        custom_parameter_3: error.code || 'unknown'
      });
    }
    
    throw error;
  });
}

/**
 * Enhanced FirestorePaginator with monitoring and error handling
 * Provides cursor-based server-side pagination
 */
export class FirestorePaginator {
  constructor(baseQuery, pageSize = 25, queryName = 'unknown') {
    this.baseQuery = baseQuery;
    this.pageSize = pageSize;
    this.queryName = queryName;
    this.lastDoc = null;
    this.hasMore = true;
    this.totalFetched = 0;
    this.isLoading = false;
  }
  
  async getNextPage() {
    if (!this.hasMore || this.isLoading) {
      return { docs: [], hasMore: false, isLoading: this.isLoading };
    }
    
    this.isLoading = true;
    
    try {
      let paginatedQuery = this.baseQuery;
      
      // Add cursor if we have one
      if (this.lastDoc) {
        paginatedQuery = startAfter(this.lastDoc);
        paginatedQuery = query(paginatedQuery, limit(this.pageSize));
      } else {
        paginatedQuery = query(paginatedQuery, limit(this.pageSize));
      }
      
      // Execute query with monitoring
      const snapshot = await monitorQuery(
        `${this.queryName}_page_${Math.floor(this.totalFetched / this.pageSize) + 1}`,
        () => getDocs(paginatedQuery)
      );
      
      const docs = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        _docRef: doc // Store for cursor
      }));
      
      // Update pagination state
      this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
      this.hasMore = snapshot.docs.length === this.pageSize;
      this.totalFetched += docs.length;
      
      console.log(`[Pagination] ${this.queryName}: fetched ${docs.length} docs, total: ${this.totalFetched}, hasMore: ${this.hasMore}`);
      
      return { docs, hasMore: this.hasMore, isLoading: false, totalFetched: this.totalFetched };
    } catch (error) {
      console.error(`[Pagination Error] ${this.queryName}:`, error);
      throw error;
    } finally {
      this.isLoading = false;
    }
  }
  
  async getPreviousPage() {
    // Note: Firestore doesn't natively support reverse pagination
    // This would require storing page boundaries or using alternative strategies
    throw new Error('Previous page navigation not implemented - requires additional cursor management');
  }
  
  reset() {
    this.lastDoc = null;
    this.hasMore = true;
    this.totalFetched = 0;
    this.isLoading = false;
    console.log(`[Pagination] ${this.queryName}: reset to beginning`);
  }
  
  getState() {
    return {
      hasMore: this.hasMore,
      isLoading: this.isLoading,
      totalFetched: this.totalFetched,
      currentPage: Math.floor(this.totalFetched / this.pageSize)
    };
  }
}

/**
 * Create optimized base query with proper ordering
 */
export function createOptimizedQuery(collection, filters = [], orderField = null, orderDirection = 'asc') {
  let q = collection;
  
  // Apply filters
  filters.forEach(filter => {
    if (filter.field && filter.operator && filter.value !== undefined) {
      q = query(q, where(filter.field, filter.operator, filter.value));
    }
  });
  
  // Apply ordering if specified
  if (orderField) {
    q = query(q, orderBy(orderField, orderDirection));
  }
  
  return q;
}

/**
 * Batch loader for related data
 * Reduces N+1 query problems
 */
export class BatchDataLoader {
  constructor(collectionRef, keyField = 'id') {
    this.collectionRef = collectionRef;
    this.keyField = keyField;
    this.cache = new Map();
  }
  
  async loadBatch(keys) {
    const uncachedKeys = keys.filter(key => !this.cache.has(key));
    
    if (uncachedKeys.length === 0) {
      return keys.map(key => this.cache.get(key));
    }
    
    // Firestore 'in' queries are limited to 10 items
    const batches = [];
    for (let i = 0; i < uncachedKeys.length; i += 10) {
      batches.push(uncachedKeys.slice(i, i + 10));
    }
    
    const results = await Promise.all(
      batches.map(async batch => {
        const q = query(this.collectionRef, where(this.keyField, 'in', batch));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      })
    );
    
    // Cache results
    results.flat().forEach(doc => {
      this.cache.set(doc[this.keyField], doc);
    });
    
    // Return all requested data
    return keys.map(key => this.cache.get(key)).filter(Boolean);
  }
  
  clearCache() {
    this.cache.clear();
  }
}

/**
 * Hook for using paginated data in React components
 */


export function usePaginatedData(baseQuery, pageSize = 25, queryName = 'query') {
  const [data, setData] = useState([]);
  const [paginator, setPaginator] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  
  // Initialize paginator when query changes
  useEffect(() => {
    if (baseQuery) {
      const newPaginator = new FirestorePaginator(baseQuery, pageSize, queryName);
      setPaginator(newPaginator);
      setData([]);
      setError(null);
      setHasMore(true);
    }
  }, [baseQuery, pageSize, queryName]);
  
  const loadMore = useCallback(async (append = true) => {
    if (!paginator || loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await paginator.getNextPage();
      
      if (append) {
        setData(prevData => [...prevData, ...result.docs]);
      } else {
        setData(result.docs);
      }
      
      setHasMore(result.hasMore);
    } catch (err) {
      setError(err.message);
      console.error('Error loading paginated data:', err);
    } finally {
      setLoading(false);
    }
  }, [paginator, loading]);
  
  const reset = useCallback(() => {
    if (paginator) {
      paginator.reset();
      setData([]);
      setHasMore(true);
      setError(null);
    }
  }, [paginator]);
  
  const refresh = useCallback(async () => {
    reset();
    await loadMore(false);
  }, [reset, loadMore]);
  
  return {
    data,
    loading,
    error,
    hasMore,
    loadMore,
    reset,
    refresh,
    paginatorState: paginator?.getState() || {}
  };
}